/**
 * @fileoverview The open target periods of the April to March financial year, as the backend
 * lists them.
 * @description Mirrors GET /sales/targets/periods (Confluence 3.5.11.12) for the mock API: the
 * periods of one type that have not ended, for the current and the next financial year, earliest
 * first, judged on today in India time. Labels are `Apr 2026`, `Q1 2026`, `H1 2026` and `2026`
 * (the year the financial year starts); a period's end date is a target's due date. Never imported
 * outside this feature's mocks/ folder.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type {
  SalesTargetPeriod,
  SalesTargetPeriodType,
} from '@/features/sales/types/sales-target.types'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** @description How many months each period type spans. */
const MONTHS_PER_PERIOD: Record<SalesTargetPeriodType, number> = {
  MONTHLY: 1,
  QUARTERLY: 3,
  BI_ANNUAL: 6,
  ANNUAL: 12,
}

/**
 * @description Formats a date as `yyyy-mm-dd`.
 * @param {number} year - The calendar year.
 * @param {number} month0 - The month, 0-indexed (0 = January); may overflow into the next year.
 * @param {number} day - The day of the month (0 = the last day of the previous month).
 * @returns {string} The ISO date.
 */
function iso(year: number, month0: number, day: number): string {
  const date = new Date(Date.UTC(year, month0, day))
  return date.toISOString().slice(0, 10)
}

/**
 * @description Today's date in India (Asia/Kolkata), which decides what has ended.
 * @param {Date} [now] - The reference time (injectable for tests).
 * @returns {string} Today as `yyyy-mm-dd`.
 */
export function indiaToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(now)
}

/**
 * @description The label of one period.
 * @param {SalesTargetPeriodType} type - The period type.
 * @param {number} fyStart - The calendar year the financial year starts.
 * @param {number} index - The period's position inside the financial year (0-based).
 * @returns {string} The label, e.g. `Q3 2026`.
 */
function labelOf(type: SalesTargetPeriodType, fyStart: number, index: number): string {
  if (type === 'ANNUAL') return String(fyStart)
  if (type === 'BI_ANNUAL') return `H${String(index + 1)} ${String(fyStart)}`
  if (type === 'QUARTERLY') return `Q${String(index + 1)} ${String(fyStart)}`
  const month0 = (3 + index) % 12
  const year = fyStart + (3 + index >= 12 ? 1 : 0)
  return `${MONTHS[month0] ?? ''} ${String(year)}`
}

/**
 * @description The periods of one type that have not ended, for the current and the next financial
 * year, earliest first.
 * @param {SalesTargetPeriodType} type - The period type.
 * @param {string} today - Today in India, as `yyyy-mm-dd`.
 * @returns {SalesTargetPeriod[]} The open periods.
 */
export function targetPeriodsFor(type: SalesTargetPeriodType, today: string): SalesTargetPeriod[] {
  const [year, month] = today.split('-').map(Number) as [number, number]
  const currentFyStart = month >= 4 ? year : year - 1
  const span = MONTHS_PER_PERIOD[type]
  const periods: SalesTargetPeriod[] = []

  for (const fyStart of [currentFyStart, currentFyStart + 1]) {
    for (let index = 0; index < 12 / span; index += 1) {
      const startMonth0 = 3 + index * span
      const startDate = iso(fyStart, startMonth0, 1)
      const endDate = iso(fyStart, startMonth0 + span, 0)
      if (endDate < today) continue
      const startLabel = `${MONTHS[new Date(`${startDate}T00:00:00Z`).getUTCMonth()] ?? ''} ${startDate.slice(0, 4)}`
      const endLabel = `${MONTHS[new Date(`${endDate}T00:00:00Z`).getUTCMonth()] ?? ''} ${endDate.slice(0, 4)}`
      periods.push({
        periodType: type,
        periodLabel: labelOf(type, fyStart, index),
        startDate,
        endDate,
        range: span === 1 ? startLabel : `${startLabel} - ${endLabel}`,
      })
    }
  }
  return periods
}
